import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import instance from '../apiInstance';
import './CompleteProfileModal.css';
import CompleteProfileFormActions from './CompleteProfileFormActions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift, faClipboardList, faHeart } from '@fortawesome/free-solid-svg-icons';
import {
  CHOCOLATE_QUAL_PLACEHOLDER,
  PERFUME_PLACEHOLDER,
  PREF_CHOCOLATE_NAME,
  PREF_CHOCOLATE_QUAL_NAME,
  PREF_PERFUMES_NAME,
  displayFieldLabel as perfumeFieldLabel,
  isPerfumeSurvey,
  isSurveyComplete as isPerfumeComplete,
  normalizePerfumeSurveyFields,
  showChocolateQual,
} from './perfumeSurveyFields';
import {
  PREF_JA_GANHOU,
  PREF_QUAL_PRESENTE,
  PREF_QUER_PRESENTE,
  PRESENTE_GANHOU_PLACEHOLDER,
  PRESENTE_QUER_PLACEHOLDER,
  displayFieldLabel as presenteFieldLabel,
  isPresenteSurvey,
  isSurveyComplete as isPresenteComplete,
  normalizePresenteSurveyFields,
  showGanhouDetail,
} from './presenteSurveyFields';

function labelFor(survey, field) {
  if (isPerfumeSurvey(survey)) return perfumeFieldLabel(field);
  if (isPresenteSurvey(survey)) return presenteFieldLabel(field);
  return field.label;
}

function completeFor(survey, fields, answers) {
  if (isPerfumeSurvey(survey)) return isPerfumeComplete(fields, answers);
  if (isPresenteSurvey(survey)) return isPresenteComplete(fields, answers);
  return fields.every((f) => !f.required || answers[f.name]);
}

const DynamicSurveyModal = () => {
  const { token } = useSelector((state) => state.auth);
  const location = useLocation();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    if (!token) return;
    if (location.pathname === '/' || location.pathname === '/register' || location.pathname === '/gate') return;

    const handleTrigger = () => {
      instance.get('/surveys/pending', { params: { context: 'geral' } })
        .then((res) => {
          if (!res.data) return;
          const sid = res.data.id;
          if (sessionStorage.getItem(`survey_session_${sid}`)) return;

          let fields = res.data.fields;
          if (isPerfumeSurvey(res.data)) fields = normalizePerfumeSurveyFields(fields);
          if (isPresenteSurvey(res.data)) fields = normalizePresenteSurveyFields(fields);

          setSurvey({ ...res.data, fields });
          const initialAnswers = {};
          fields.forEach((f) => { initialAnswers[f.name] = ''; });
          setAnswers(initialAnswers);
          window.dispatchEvent(new Event('geral_survey_open'));
        })
        .catch((err) => console.error('Error fetching pending survey:', err));
    };

    window.addEventListener('trigger_survey', handleTrigger);
    window.addEventListener('cadastro_completed', handleTrigger);
    handleTrigger();
    return () => {
      window.removeEventListener('trigger_survey', handleTrigger);
      window.removeEventListener('cadastro_completed', handleTrigger);
    };
  }, [token, location.pathname]);

  const closeSurvey = () => {
    setSurvey(null);
    window.dispatchEvent(new Event('geral_survey_closed'));
  };

  const handleChange = (name, value) => {
    const next = { ...answers, [name]: value };
    if (name === PREF_CHOCOLATE_NAME && value === 'Não gosto de chocolate') {
      next[PREF_CHOCOLATE_QUAL_NAME] = '';
    }
    if (name === PREF_JA_GANHOU && value === 'Não') {
      next[PREF_QUAL_PRESENTE] = '';
    }
    setAnswers(next);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!completeFor(survey, survey.fields, answers)) {
      setError('Por favor, preencha os campos obrigatórios.');
      return;
    }

    setLoading(true);
    try {
      const res = await instance.post(`/surveys/${survey.id}/submit`, { answers });

      if (res.data?.points_awarded > 0) {
        alert(`Parabéns! Você ganhou ${res.data.points_awarded} pontos!`);
      }

      sessionStorage.setItem(`survey_session_${survey.id}`, 'true');
      closeSurvey();
    } catch (err) {
      console.error('Error submitting survey:', err);
      setError('Erro ao enviar respostas. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!survey) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const blockEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', blockEscape, true);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', blockEscape, true);
    };
  }, [survey]);

  if (!survey) return null;

  const canSubmit = completeFor(survey, survey.fields, answers) && !loading;
  const chocolateQualVisible = showChocolateQual(answers);
  const ganhouDetailVisible = showGanhouDetail(answers);
  const isPresente = isPresenteSurvey(survey);

  return (
    <div className="complete-profile-overlay">
      <div className="complete-profile-modal">
        <div className="complete-profile-header">
          {isPresente ? (
            <div style={{ position: 'relative', display: 'inline-block', marginBottom: 16, width: 56, height: 48 }}>
              <FontAwesomeIcon icon={faGift} size="3x" color="#D4AF37" />
              <FontAwesomeIcon
                icon={faHeart}
                style={{ position: 'absolute', right: -10, bottom: 4, color: '#E85D75', fontSize: 22 }}
              />
            </div>
          ) : (
            <FontAwesomeIcon icon={faClipboardList} size="3x" color="#D4AF37" style={{ marginBottom: 16 }} />
          )}
          <h2>{survey.title}</h2>
          {survey.description && <p>{survey.description}</p>}

          {survey.reward_points > 0 && (
            <div className="complete-profile-points-badge">
              <FontAwesomeIcon icon={faGift} style={{ marginRight: 6 }} />
              Ganhe {survey.reward_points} Pontos
            </div>
          )}
        </div>

        <form className="complete-profile-form" onSubmit={handleSubmit}>
          <div className="complete-profile-form-body">
          {survey.fields.map((field, idx) => {
            if (field.name === PREF_CHOCOLATE_QUAL_NAME && !chocolateQualVisible) return null;
            if (field.name === PREF_QUAL_PRESENTE && !ganhouDetailVisible) return null;
            const perfumeQualRequired = isPerfumeSurvey(survey)
              && field.name === PREF_CHOCOLATE_QUAL_NAME
              && chocolateQualVisible;
            const perfumeTextRequired = isPerfumeSurvey(survey) && field.name === PREF_PERFUMES_NAME;
            return (
              <div key={idx} className="complete-profile-input-group">
                <label>
                  {labelFor(survey, field)}
                  {(field.required || perfumeQualRequired || perfumeTextRequired) && ' *'}
                </label>
                {field.name === PREF_CHOCOLATE_NAME && (
                  <span className="complete-profile-field-hint">Ou selecione que não gosta</span>
                )}
                {field.name === PREF_CHOCOLATE_QUAL_NAME && chocolateQualVisible && (
                  <span className="complete-profile-field-hint">Obrigatório — escreve marca ou tipo que você curte</span>
                )}
                {field.name === PREF_PERFUMES_NAME && (
                  <span className="complete-profile-field-hint">Nomeie pelo menos um perfume de verdade (mín. 3 letras)</span>
                )}
                {field.name === PREF_QUER_PRESENTE && (
                  <span className="complete-profile-field-hint">Pode ser lenço, acessório, bolsa...</span>
                )}

                {field.type === 'text' && (
                  <input
                    type="text"
                    value={answers[field.name] || ''}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    required={field.required}
                    placeholder={
                      field.name === PREF_PERFUMES_NAME ? PERFUME_PLACEHOLDER
                        : field.name === PREF_CHOCOLATE_QUAL_NAME ? CHOCOLATE_QUAL_PLACEHOLDER
                          : field.name === PREF_QUAL_PRESENTE ? PRESENTE_GANHOU_PLACEHOLDER
                            : field.name === PREF_QUER_PRESENTE ? PRESENTE_QUER_PLACEHOLDER
                              : undefined
                    }
                  />
                )}

                {field.type === 'select' && (
                  <select
                    className="complete-profile-select"
                    value={answers[field.name] || ''}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    required={field.required}
                  >
                    <option value="">Selecione uma opção</option>
                    {field.options?.map((opt, i) => (
                      <option key={i} value={opt}>{opt}</option>
                    ))}
                  </select>
                )}

                {field.type === 'radio' && (
                  <div className="complete-profile-radio-group">
                    {field.options?.map((opt, i) => (
                      <div className="complete-profile-radio-option" key={i}>
                        <input
                          type="radio"
                          name={field.name}
                          id={`${field.name}-${i}`}
                          value={opt}
                          checked={answers[field.name] === opt}
                          onChange={(e) => handleChange(field.name, e.target.value)}
                          onClick={(e) => handleChange(field.name, e.target.value)}
                          required={field.required && !answers[field.name]}
                        />
                        <label className="complete-profile-radio-label" htmlFor={`${field.name}-${i}`}>
                          {opt}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          </div>
          <CompleteProfileFormActions
            error={error}
            hint="Flor, preencha para continuar"
            showHint={!canSubmit}
            loading={loading}
            canSubmit={canSubmit}
            submitLabel={survey.reward_points > 0 ? 'Enviar e Ganhar Pontos' : 'Enviar'}
          />
        </form>
      </div>
    </div>
  );
};

export default DynamicSurveyModal;
